import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  FormSelect,
  VirtualizedFormSelect,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@roger/ui-components';
import { Button } from '@roger/ui-components';
import {
  Loader2,
  CheckCircle,
  XCircle,
  Zap,
  ChevronRight,
  AlertCircle,
  Check,
  HelpCircle,
} from 'lucide-react';
import { useToast } from '../../hooks/useToastContext';
import { eligibilityBlockReason } from '../eligibility/eligibilityBlock';
import { checkEligibility, getEligibilityChecks } from '../../services/api';
import type { IntakeRecord } from '../../store/slices/intakeSlice';
import type { EligibilityCheckResponse } from '../../services/api';
import {
  getEffectiveStatus,
  type EffectiveEligibilityStatus,
} from '../eligibility/eligibilityStatus';
import EligibilityDetailsModal from '../EligibilityDetailsModal';
import {
  formatDateTimeInAgencyZone,
  formatDateToMMDDYYYY,
} from '../../utils/dateUtils';
import { Field, DateField } from '../FormFields';
import { formatZipCode } from '../../utils/intake';
import { runValidation } from './validators';
import { US_STATES, US_STATE_CODE_OPTIONS } from '../../constants/options';
import { useIntake } from '../../hooks/useIntake';
import { getSchedulingEmrForClinic } from '../../utils/intakeSchedulingEmr';
import { useStore } from '../../store';
import { IntakeTabValidationKey } from '../../store/slices/intakeSlice';
import MarkCompletedButton from './MarkCompletedButton';
import {
  formatEligibilityErrorForDisplay,
  GENERIC_ERROR_MESSAGE,
  type StediAAAError,
} from '../../utils/eligibilityErrorMessages';
import { AiExtractedInsuranceSuggestion } from './AiExtractedInsuranceSuggestion';
import { IntakeInsuranceSection } from './IntakeInsuranceSection';
import { useIntakeFormSync } from '../../hooks/useIntakeFormSync';
import { useIntakeFieldSave } from '../../hooks/useIntakeFieldSave';
import { FeatureFlag } from '../../constants/featureFlags';

const ELIGIBILITY_PDF_PREFIX = 'Eligibility_Check_';
const ELIGIBILITY_PDF_POLL_INTERVAL_MS = 3_000;
const ELIGIBILITY_PDF_POLL_MAX_ATTEMPTS = 20;

function countEligibilityPdfDocuments(
  documents: Array<{ file_name?: string | null }>
): number {
  return documents.filter(d => d.file_name?.startsWith(ELIGIBILITY_PDF_PREFIX))
    .length;
}

const US_STATE_CODES = new Set<string>(US_STATES.map(s => s.value));

/** Patient-information fields validated on this tab (ROG-3025). These values
 * are sent verbatim in the outbound X12 270 eligibility request, so garbage
 * here produces payer rejections downstream. */
const PATIENT_INFO_FIELDS = [
  'patient_first_name',
  'patient_last_name',
  'patient_dob',
  'patient_street_address',
  'patient_city',
  'patient_state',
  'patient_zip_code',
] as const;

type PatientInfoField = (typeof PATIENT_INFO_FIELDS)[number];

const PATIENT_INFO_FIELD_SET: ReadonlySet<string> = new Set(
  PATIENT_INFO_FIELDS
);

const validatePatientInfoField = (
  field: PatientInfoField,
  value: string
): string | null => {
  switch (field) {
    case 'patient_first_name':
      return value.trim() ? null : 'First name is required';
    case 'patient_last_name':
      return value.trim() ? null : 'Last name is required';
    case 'patient_dob':
      return value.trim() ? null : 'Date of birth is required';
    case 'patient_street_address':
      return value.trim() ? null : 'Street address is required';
    case 'patient_city':
      return value.trim() ? null : 'City is required';
    case 'patient_state':
      if (!value.trim()) return null;
      return US_STATE_CODES.has(value.trim().toUpperCase())
        ? null
        : 'Select a valid state';
    case 'patient_zip_code':
      return runValidation('validate_zip', value);
  }
};

interface EligibilityCheckStepProps {
  selectedIntake: IntakeRecord | null;
  onRefresh?: () => Promise<void>;
  refreshDocuments?: () => Promise<unknown>;
  agencyInsuranceOptions: Array<{ value: string; label: string }>;
  agencyInsurancesLoading: boolean;
  agencyInsurancesError: string | null;
  onStatusChange?: () => void | Promise<void>;
  documentNameDict?: Record<string, string>;
}

const EligibilityCheckStep: React.FC<EligibilityCheckStepProps> = ({
  selectedIntake,
  onRefresh,
  refreshDocuments,
  agencyInsuranceOptions,
  agencyInsurancesLoading,
  agencyInsurancesError,
  onStatusChange,
  documentNameDict,
}) => {
  const [isRunningCheck, setIsRunningCheck] = useState(false);
  const [pastEligibilityResults, setPastEligibilityResults] = useState<
    EligibilityCheckResponse[]
  >([]);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [selectedEligibilityResult, setSelectedEligibilityResult] =
    useState<EligibilityCheckResponse | null>(null);
  const [isEligibilityModalOpen, setIsEligibilityModalOpen] = useState(false);
  const { success, error } = useToast();
  const { updateIntakeRecord } = useIntake();
  const setIntakeTabValid = useStore(state => state.setIntakeTabValid);
  const hasFeatureFlag = useStore(state => state.hasFeatureFlag);
  const agencyClinics = useStore(state => state.agencyClinics);
  const agency = useStore(state => state.user?.agency);

  // The insurance section only feeds push-to-Roger onboarding, so Kinnser
  // intakes must not see it (EMR-886 review). Resolved with the shared
  // clinic/agency/flags resolver rather than reading push_to_roger directly,
  // so dual-capable clinics fall back to their configured default EMR the
  // same way the Extraction and Scheduling steps do.
  const schedulingEmr = useMemo(
    () =>
      getSchedulingEmrForClinic(
        agencyClinics,
        selectedIntake?.agency_clinic_id,
        {
          agency: {
            is_kinnser_enabled: agency?.is_kinnser_enabled,
            is_rogeremr_enabled: agency?.is_rogeremr_enabled,
          },
          flags: selectedIntake,
        }
      ),
    [
      agencyClinics,
      selectedIntake?.agency_clinic_id,
      agency?.is_kinnser_enabled,
      agency?.is_rogeremr_enabled,
      selectedIntake?.roger_create_episode,
      selectedIntake?.kinnser_create_episode,
      selectedIntake?.push_to_roger,
      selectedIntake?.push_to_kinnser,
    ]
  );
  const showInsuranceSection = schedulingEmr === 'roger';
  const showAIExplanations = hasFeatureFlag(
    FeatureFlag.INTAKE_SEE_AI_EXPLANATION
  );

  // Editable form state — synced from selectedIntake with keepDirtyValues
  // so in-progress edits aren't clobbered by save responses.
  const [formData, setFormData] = useIntakeFormSync({
    selectedIntake,
    extract: (intake: IntakeRecord) => ({
      agency_insurance_id: intake.agency_insurance_id || '',
      primary_member_id: intake.primary_member_id || '',
      patient_first_name: intake.patient_first_name || '',
      patient_last_name: intake.patient_last_name || '',
      patient_dob: (intake.patient_dob as string) || '',
      patient_street_address: intake.patient_street_address || '',
      patient_city: intake.patient_city || '',
      patient_state: intake.patient_state || '',
      patient_zip_code: intake.patient_zip_code || '',
    }),
    keepDirtyValues: true,
  });

  // Save utilities — status indicator + debounce + immediate save
  const {
    saveStatus,
    saveField,
    debouncedSave,
    cancelDebounce,
    hasPendingDebounce,
  } = useIntakeFieldSave({
    intakeId: selectedIntake?.id,
    updateIntakeRecord,
    onAfterSave: onRefresh,
  });

  // Validation errors derived from current values (gate-on-value): required
  // fields count against validity even before the user touches them, so an
  // untouched-but-incomplete form still blocks Push to EMR and the
  // eligibility check.
  const validationErrors = useMemo(() => {
    const errors = {} as Record<PatientInfoField, string | null>;
    for (const field of PATIENT_INFO_FIELDS) {
      errors[field] = validatePatientInfoField(field, formData[field] || '');
    }
    return errors;
  }, [formData]);

  const hasValidationErrors = PATIENT_INFO_FIELDS.some(
    field => validationErrors[field] !== null
  );

  // Show-on-touch: error TEXT appears only after the user has blurred the
  // field — except when a non-empty stored value is malformed (e.g. legacy
  // free-text state), which should explain itself immediately.
  const [touchedFields, setTouchedFields] = useState<
    Partial<Record<PatientInfoField, boolean>>
  >({});

  const markTouched = useCallback((field: PatientInfoField) => {
    setTouchedFields(prev => (prev[field] ? prev : { ...prev, [field]: true }));
  }, []);

  // Reset touch state when switching intakes so one record's blurs don't
  // surface error text on the next record's untouched form.
  useEffect(() => {
    setTouchedFields({});
  }, [selectedIntake?.id]);

  const visibleError = useCallback(
    (field: PatientInfoField): string | undefined => {
      const error = validationErrors[field];
      if (!error) return undefined;
      if (touchedFields[field] || (formData[field] || '').trim() !== '') {
        return error;
      }
      return undefined;
    },
    [validationErrors, touchedFields, formData]
  );

  // Report this tab's validation state so Push to EMR can block onboarding
  // when patient information is incomplete or malformed. On unmount (e.g.
  // switching to Push to EMR), cleanup writes last-known validity so the
  // store stays correct.
  const tabIsValid = !hasValidationErrors;
  const lastValidRef = useRef(tabIsValid);
  lastValidRef.current = tabIsValid;
  useEffect(() => {
    setIntakeTabValid(IntakeTabValidationKey.Eligibility, tabIsValid);
    return () => {
      setIntakeTabValid(
        IntakeTabValidationKey.Eligibility,
        lastValidRef.current
      );
    };
  }, [setIntakeTabValid, tabIsValid]);

  // Ref for insurance select to manually set value
  const insuranceSelectRef = useRef<HTMLInputElement>(null);
  // Ref for state select to sync value from formData (FormSelect is
  // uncontrolled; it reads its hidden input's value)
  const stateSelectRef = useRef<HTMLInputElement>(null);
  const pdfPollCancelledRef = useRef(false);

  // Set insurance select value when formData changes or options load
  useEffect(() => {
    if (insuranceSelectRef.current && formData.agency_insurance_id) {
      insuranceSelectRef.current.value = formData.agency_insurance_id;
      insuranceSelectRef.current.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    }
  }, [formData.agency_insurance_id, agencyInsuranceOptions]);

  // Sync the state dropdown's display with formData. A legacy free-text value
  // that isn't a valid state code renders as the placeholder (with a
  // validation error) rather than being silently cleared — the stored value
  // is only replaced when the user picks a state. Deliberately no change
  // event is dispatched here: firing one would trigger the save handler and
  // overwrite a legacy value with ''.
  useEffect(() => {
    if (!stateSelectRef.current) return;
    const storedState = (formData.patient_state || '').trim().toUpperCase();
    const selectValue = US_STATE_CODES.has(storedState) ? storedState : '';
    if (stateSelectRef.current.value !== selectValue) {
      stateSelectRef.current.value = selectValue;
    }
  }, [formData.patient_state]);

  // Field change handler with debounced auto-save for text fields
  const handleFieldChange = useCallback(
    (fieldName: keyof typeof formData, value: string, immediate = false) => {
      setFormData(prev => ({ ...prev, [fieldName]: value }));

      if (immediate) {
        void saveField(fieldName, value);
      } else {
        debouncedSave(fieldName, value);
      }
    },
    [setFormData, saveField, debouncedSave]
  );

  // Field blur handler for immediate save + validation feedback
  const handleFieldBlur = useCallback(
    (fieldName: keyof typeof formData) => {
      cancelDebounce(fieldName);

      if (PATIENT_INFO_FIELD_SET.has(fieldName)) {
        markTouched(fieldName as PatientInfoField);
      }

      const currentValue = formData[fieldName];
      const originalValue = selectedIntake?.[fieldName] || '';
      if (currentValue !== originalValue) {
        void saveField(fieldName, currentValue);
      }
    },
    [formData, selectedIntake, saveField, cancelDebounce, markTouched]
  );

  // Format status text for display
  const getStatusText = (status: EffectiveEligibilityStatus): string => {
    const statusMap: Record<string, string> = {
      pending: 'Pending',
      completed: 'Completed',
      failed: 'Failed',
      expired: 'Expired',
      in_progress: 'In progress',
      cancelled: 'Cancelled',
      completed_with_error: 'Completed with error',
    };

    return statusMap[status] || String(status);
  };

  const handleViewDetails = (result: EligibilityCheckResponse) => {
    setSelectedEligibilityResult(result);
    setIsEligibilityModalOpen(true);
  };

  // Fetch past eligibility check results
  const fetchPastEligibilityResults = async () => {
    if (!selectedIntake?.id) return;

    setIsLoadingResults(true);
    try {
      const response = await getEligibilityChecks({
        query: { intake_id: selectedIntake.id },
      });

      if (response.data) {
        // Sort: primary checks first (no parent_eligibility_check_id), then by request date descending
        const sortedResults = [...response.data].sort((a, b) => {
          const aPrimary = !a.parent_eligibility_check_id;
          const bPrimary = !b.parent_eligibility_check_id;
          if (aPrimary !== bPrimary) return aPrimary ? -1 : 1;
          return (
            new Date(b.request_date).getTime() -
            new Date(a.request_date).getTime()
          );
        });
        setPastEligibilityResults(sortedResults);
      }
    } catch (err) {
      console.error('Error fetching past eligibility results:', err);
    } finally {
      setIsLoadingResults(false);
    }
  };

  // Load past results when intake changes
  useEffect(() => {
    fetchPastEligibilityResults();
  }, [selectedIntake?.id]);

  // PDF poll runs fire-and-forget after the check; cancel it on unmount or intake change
  // so refreshDocuments() does not keep firing after the user leaves this screen.
  useEffect(() => {
    pdfPollCancelledRef.current = false;
    return () => {
      pdfPollCancelledRef.current = true;
    };
  }, [selectedIntake?.id]);

  const handleRunEligibilityCheck = async () => {
    if (!selectedIntake) return;

    setIsRunningCheck(true);
    const eligibilityPdfBaseline = countEligibilityPdfDocuments(
      selectedIntake.documents ?? []
    );

    try {
      const result = await checkEligibility({
        path: { intake_id: selectedIntake.id },
      });

      // Check if the API call itself failed (non-200 response)
      if (result.error) {
        // Try to extract AAA errors from the error response
        const errorData = result.error as Record<string, unknown>;
        const aaaErrors = (errorData?.stedi_errors || errorData?.errors) as
          StediAAAError[] | undefined;
        const formattedError = formatEligibilityErrorForDisplay(
          null,
          aaaErrors
        );
        const errorMessage =
          formattedError?.message || GENERIC_ERROR_MESSAGE.message;
        const suggestion =
          formattedError?.suggestion || GENERIC_ERROR_MESSAGE.suggestion;
        error('Eligibility Check Failed', `${errorMessage}\n\n${suggestion}`);
      }
      // Check if the result has an error message or errors (failed eligibility check)
      else if (
        result.data?.error_message ||
        (result.data?.raw_response?.errors as StediAAAError[] | undefined)
          ?.length
      ) {
        const formattedError = formatEligibilityErrorForDisplay(
          result.data.error_message,
          result.data.raw_response?.errors as StediAAAError[] | undefined
        );
        const errorMessage =
          formattedError?.message || GENERIC_ERROR_MESSAGE.message;
        const suggestion =
          formattedError?.suggestion || GENERIC_ERROR_MESSAGE.suggestion;
        error('Eligibility Check Failed', `${errorMessage}\n\n${suggestion}`);
      } else {
        success(
          'Eligibility Check Complete',
          'Eligibility check completed successfully.'
        );
      }

      // Refresh the intake data and eligibility results
      if (onRefresh) {
        await onRefresh();
      }
      await fetchPastEligibilityResults();

      // PDFs attach asynchronously; refresh until new eligibility PDFs show in viewer
      if (refreshDocuments && !result.error) {
        void (async () => {
          for (let i = 0; i < ELIGIBILITY_PDF_POLL_MAX_ATTEMPTS; i++) {
            if (pdfPollCancelledRef.current) return;
            await new Promise(r =>
              setTimeout(r, ELIGIBILITY_PDF_POLL_INTERVAL_MS)
            );
            if (pdfPollCancelledRef.current) return;
            const docs = (await refreshDocuments()) as Array<{
              file_name?: string | null;
            }>;
            if (countEligibilityPdfDocuments(docs) > eligibilityPdfBaseline) {
              return;
            }
          }
        })();
      }
    } catch (err: unknown) {
      console.error('Error running eligibility check:', err);
      // Show generic error message for unexpected errors
      error(
        'Eligibility Check Failed',
        `${GENERIC_ERROR_MESSAGE.message}\n\n${GENERIC_ERROR_MESSAGE.suggestion}`
      );
    } finally {
      setIsRunningCheck(false);
    }
  };

  // Why the mapped payer itself cannot be checked, in the same words the chart and
  // the server use. Undefined until an insurance is mapped, because "no payer yet"
  // is a step the user has not reached rather than a payer that cannot be checked.
  const payerBlockReason = selectedIntake?.agency_insurance
    ? eligibilityBlockReason({
        // Resolved by the server from the payer, the same way the chart and the
        // gate resolve it. Deriving it here from edi_payer_id would answer a
        // different question: that is a claims identifier, and a payer can carry
        // one while Stedi cannot do eligibility for it.
        supported: Boolean(
          selectedIntake.agency_insurance.eligibility_supported
        ),
        enrollmentStatus:
          selectedIntake.agency_insurance.eligibility_enrollment_status,
        hasMemberId: Boolean(formData.primary_member_id?.trim()),
      })
    : undefined;

  // Determine if we can run eligibility check. Patient-information errors
  // gate the check too — these fields go verbatim into the X12 270 request.
  const canRunEligibilityCheck =
    selectedIntake &&
    selectedIntake.agency_clinic_id &&
    formData.agency_insurance_id &&
    !payerBlockReason &&
    !hasValidationErrors;

  return (
    <Card className='h-full flex flex-col'>
      <CardHeader className='border-b py-3 px-4'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            {/* Save Status Indicator */}
            {saveStatus === 'saving' && (
              <div className='flex items-center gap-2 text-xs text-gray-700'>
                <Loader2 className='h-3 w-3 animate-spin' />
                <span>Saving...</span>
              </div>
            )}
            {saveStatus === 'saved' && (
              <div className='flex items-center gap-2 text-xs text-green-600'>
                <Check className='h-3 w-3' />
                <span>Saved</span>
              </div>
            )}
            {saveStatus === 'error' && (
              <div className='flex items-center gap-2 text-xs text-red-600'>
                <AlertCircle className='h-3 w-3' />
                <span>Save failed</span>
              </div>
            )}
          </div>
          {selectedIntake && (
            <MarkCompletedButton
              intakeId={selectedIntake.id}
              tabKey='eligibility'
              isComplete={
                selectedIntake.tab_statuses?.eligibility === 'complete' ||
                selectedIntake.tab_statuses?.eligibility ===
                  'complete_with_errors'
              }
              onStatusChange={onStatusChange}
              className='ml-2'
            />
          )}
        </div>
      </CardHeader>
      <CardContent className='flex-1 overflow-y-auto p-4 pt-3'>
        <div className='space-y-6'>
          {/* Current Status */}
          <div className='space-y-4'>
            {/* Two Column Layout for Insurance and Patient */}
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
              {/* Insurance Information */}
              <div className='border px-4 py-3 space-y-4'>
                <div className='font-semibold mb-2'>Insurance Details</div>

                {/* Mapped Insurance Selection */}
                <div>
                  {!selectedIntake?.agency_clinic_id ? (
                    <div className='text-sm text-orange-700 bg-orange-50 p-2 rounded'>
                      Please select a clinic first to see available insurances
                    </div>
                  ) : (
                    <div>
                      <div className='flex items-center gap-2 mb-2'>
                        <label className='text-sm font-medium text-gray-700'>
                          Primary Insurance
                        </label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button className='text-gray-600 hover:text-gray-800 transition-colors'>
                              <HelpCircle className='h-4 w-4' />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className='w-72' align='start'>
                            <div className='space-y-2 text-sm'>
                              <p>
                                These are insurances configured for your clinic
                                in Kinnser.
                              </p>
                              <p className='text-gray-600'>
                                <span>🟢</span> indicates the insurance supports
                                eligibility checks.
                              </p>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <VirtualizedFormSelect
                        ref={insuranceSelectRef}
                        label=''
                        placeholder={
                          agencyInsurancesLoading
                            ? 'Loading insurances...'
                            : agencyInsurancesError
                              ? 'Error loading insurances'
                              : 'Select insurance from your EMR'
                        }
                        options={agencyInsuranceOptions}
                        required={true}
                        disabled={agencyInsurancesLoading}
                        // Deliberately no error here. Most of an agency's plan
                        // catalogue is payers Stedi cannot check, so marking the
                        // field invalid reads as a mistake the user made rather
                        // than an ordinary property of the payer. The reason is
                        // stated once, by the run button below.
                        name='agency_insurance_id'
                        onChange={e => {
                          handleFieldChange(
                            'agency_insurance_id',
                            e.target.value,
                            true
                          );
                        }}
                      />
                      <div className='text-xs text-gray-700 mt-1'>
                        ✓ = supports eligibility checks
                      </div>
                      {selectedIntake?.primary_insurance && (
                        <AiExtractedInsuranceSuggestion
                          value={selectedIntake?.primary_insurance}
                          showAIExplanations={showAIExplanations}
                          fieldExplanation={
                            selectedIntake.fields_explanations
                              ?.primary_insurance
                          }
                          fieldsExplanations={
                            selectedIntake.fields_explanations
                          }
                          documentNameDict={documentNameDict}
                        />
                      )}
                    </div>
                  )}
                </div>

                {/* Member ID */}
                <div>
                  <Field
                    label='Member ID / Policy Number'
                    value={formData.primary_member_id}
                    htmlFor='primary_member_id'
                    required={true}
                    warning={!formData.primary_member_id}
                    error={
                      !formData.primary_member_id
                        ? 'Member ID is required for eligibility check'
                        : undefined
                    }
                    onChange={value =>
                      handleFieldChange('primary_member_id', value)
                    }
                    onBlur={() => handleFieldBlur('primary_member_id')}
                  />
                </div>
              </div>

              {/* Patient Information - Editable */}
              <div className='border px-4 py-3 space-y-3'>
                <div className='font-semibold mb-2'>Patient Information</div>

                {/* Patient Name */}
                <div className='grid grid-cols-2 gap-3'>
                  <Field
                    label='First Name'
                    value={formData.patient_first_name}
                    htmlFor='patient_first_name'
                    required={true}
                    error={visibleError('patient_first_name')}
                    onChange={value =>
                      handleFieldChange('patient_first_name', value)
                    }
                    onBlur={() => handleFieldBlur('patient_first_name')}
                  />
                  <Field
                    label='Last Name'
                    value={formData.patient_last_name}
                    htmlFor='patient_last_name'
                    required={true}
                    error={visibleError('patient_last_name')}
                    onChange={value =>
                      handleFieldChange('patient_last_name', value)
                    }
                    onBlur={() => handleFieldBlur('patient_last_name')}
                  />
                </div>

                {/* Date of Birth */}
                <DateField
                  label='Date of Birth'
                  value={
                    typeof formData.patient_dob === 'string'
                      ? formData.patient_dob
                      : undefined
                  }
                  htmlFor='patient_dob'
                  required={true}
                  error={visibleError('patient_dob')}
                  yearRange={{
                    fromYear: 1900,
                    toYear: new Date().getFullYear(),
                  }}
                  onChange={value => {
                    markTouched('patient_dob');
                    handleFieldChange('patient_dob', value || '', true);
                  }}
                  onBlur={() => markTouched('patient_dob')}
                />

                {/* Address */}
                <Field
                  label='Street Address'
                  value={formData.patient_street_address}
                  htmlFor='patient_street_address'
                  required={true}
                  error={visibleError('patient_street_address')}
                  onChange={value =>
                    handleFieldChange('patient_street_address', value)
                  }
                  onBlur={() => handleFieldBlur('patient_street_address')}
                />
                <div className='grid grid-cols-3 gap-3'>
                  <Field
                    label='City'
                    value={formData.patient_city}
                    htmlFor='patient_city'
                    required={true}
                    error={visibleError('patient_city')}
                    onChange={value => handleFieldChange('patient_city', value)}
                    onBlur={() => handleFieldBlur('patient_city')}
                  />
                  <FormSelect
                    ref={stateSelectRef}
                    label='State'
                    placeholder='Select state'
                    options={US_STATE_CODE_OPTIONS}
                    error={visibleError('patient_state')}
                    name='patient_state'
                    onChange={e => {
                      handleFieldChange('patient_state', e.target.value, true);
                    }}
                    onBlur={() => handleFieldBlur('patient_state')}
                  />
                  <Field
                    label='Zip Code'
                    value={formData.patient_zip_code}
                    htmlFor='patient_zip_code'
                    error={visibleError('patient_zip_code')}
                    maintainCursorPosition
                    onChange={value =>
                      handleFieldChange(
                        'patient_zip_code',
                        formatZipCode(value)
                      )
                    }
                    onBlur={() => handleFieldBlur('patient_zip_code')}
                  />
                </div>
              </div>
            </div>

            {/* Patient Insurance section (EMR-886) — sparse overrides consumed
                when the intake is pushed to Roger. Hidden for Kinnser intakes,
                which never reach that onboarding path. */}
            {showInsuranceSection && (
              <IntakeInsuranceSection
                key={selectedIntake?.id}
                selectedIntake={selectedIntake}
                formData={formData}
                pastEligibilityResults={pastEligibilityResults}
                saveField={saveField}
                debouncedSave={debouncedSave}
                cancelDebounce={cancelDebounce}
                hasPendingDebounce={hasPendingDebounce}
              />
            )}

            {/* Insurance Verification Status */}
            <div className='border px-4 py-3 flex items-center gap-3'>
              {!selectedIntake?.insurance_verified ? (
                <XCircle className='h-4 w-4 flex-shrink-0' />
              ) : (
                <CheckCircle className='h-4 w-4 flex-shrink-0' />
              )}
              <div className='flex-1'>
                <div className='font-medium text-sm'>
                  Insurance Verification:{' '}
                  {selectedIntake?.insurance_verified
                    ? 'Verified'
                    : 'Not Verified'}
                </div>
              </div>
            </div>
          </div>

          {/* Run Eligibility Check */}
          <div className='space-y-4 pt-2'>
            <Button
              onClick={handleRunEligibilityCheck}
              disabled={isRunningCheck || !canRunEligibilityCheck}
              className='w-full'
              size='lg'
            >
              {isRunningCheck ? (
                <>
                  <Loader2 className='h-3 w-3 animate-spin mr-2' />
                  Checking Eligibility...
                </>
              ) : (
                <>
                  <Zap className='h-3 w-3 mr-2' />
                  Run Eligibility Check
                </>
              )}
            </Button>
            {!canRunEligibilityCheck && selectedIntake && (
              // Muted rather than orange: an unavailable check is usually just what
              // this payer is, not something the user left undone.
              <div className='text-xs text-muted-foreground text-center'>
                {[
                  !selectedIntake.agency_clinic_id && 'Select a clinic',
                  !formData.agency_insurance_id && 'Map to an insurance',
                  payerBlockReason,
                  hasValidationErrors && 'Complete patient information',
                ]
                  .filter(Boolean)
                  .join(' • ')}
              </div>
            )}
          </div>

          {/* Past Eligibility Check Results */}
          {selectedIntake?.id && (
            <div className='space-y-4 pt-4 border-t'>
              <div className='font-semibold'>
                Past Eligibility Check Results
              </div>

              {isLoadingResults ? (
                <div className='flex items-center gap-2 text-gray-700'>
                  <Loader2 className='h-3 w-3 animate-spin' />
                  Loading past results...
                </div>
              ) : pastEligibilityResults.length === 0 ? (
                <div className='text-gray-600 italic text-sm'>
                  No previous eligibility checks found for this patient.
                </div>
              ) : (
                <div className='space-y-3'>
                  {pastEligibilityResults.map(result => {
                    const effectiveStatus = getEffectiveStatus(result);
                    return (
                      <div
                        key={result.id}
                        className='border rounded-lg p-3 bg-gray-50 hover:bg-gray-100 hover:border-gray-300 transition-all cursor-pointer group'
                        onClick={() => handleViewDetails(result)}
                        role='button'
                        tabIndex={0}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleViewDetails(result);
                          }
                        }}
                      >
                        <div className='flex items-start gap-3'>
                          {/* Status Icon */}
                          {effectiveStatus === 'completed' ? (
                            <CheckCircle className='h-4 w-4 text-green-600 mt-0.5 flex-shrink-0' />
                          ) : effectiveStatus === 'completed_with_error' ? (
                            <XCircle className='h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0' />
                          ) : effectiveStatus === 'failed' ? (
                            <XCircle className='h-4 w-4 text-red-600 mt-0.5 flex-shrink-0' />
                          ) : effectiveStatus === 'pending' ||
                            effectiveStatus === 'in_progress' ? (
                            <Loader2 className='h-4 w-4 text-yellow-600 animate-spin mt-0.5 flex-shrink-0' />
                          ) : (
                            <XCircle className='h-4 w-4 text-gray-600 mt-0.5 flex-shrink-0' />
                          )}

                          <div className='flex-1 min-w-0'>
                            {/* Header */}
                            <div className='flex items-center justify-between mb-2'>
                              <div className='font-medium text-sm'>
                                {result.payor_name && (
                                  <span className='text-gray-900'>
                                    {result.payor_name}
                                    {' — '}
                                  </span>
                                )}
                                <span className='font-medium'>Status:</span>{' '}
                                <span className='text-gray-600'>
                                  {getStatusText(effectiveStatus)}
                                </span>
                                {result.parent_eligibility_check_id && (
                                  <span className='inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800'>
                                    Related entity
                                  </span>
                                )}
                              </div>
                              <div className='flex items-center gap-2'>
                                <div className='text-xs text-gray-700'>
                                  {formatDateTimeInAgencyZone(
                                    result.request_date
                                  )}
                                </div>
                                <ChevronRight className='h-4 w-4 text-gray-600 group-hover:text-gray-800 transition-colors' />
                              </div>
                            </div>

                            {/* Error Message - User-friendly display */}
                            {(() => {
                              const formattedError =
                                formatEligibilityErrorForDisplay(
                                  result.error_message,
                                  result.raw_response?.errors as
                                    StediAAAError[] | undefined
                                );
                              if (!formattedError) return null;
                              return (
                                <div className='text-sm mb-2 space-y-1'>
                                  <div>
                                    <span className='font-medium'>Error:</span>{' '}
                                    <span className='text-gray-700'>
                                      {formattedError.message}
                                    </span>
                                  </div>
                                  {formattedError.suggestion && (
                                    <div>
                                      <span className='font-medium'>
                                        Suggestion:
                                      </span>{' '}
                                      <span className='text-gray-600'>
                                        {formattedError.suggestion}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Effective/Termination Dates */}
                            {result.effective_date && (
                              <div className='text-sm mb-2'>
                                <span className='font-medium'>Effective:</span>{' '}
                                <span className='text-gray-700'>
                                  {formatDateToMMDDYYYY(result.effective_date)}
                                  {result.termination_date && (
                                    <>
                                      {' '}
                                      -{' '}
                                      {formatDateToMMDDYYYY(
                                        result.termination_date
                                      )}
                                    </>
                                  )}
                                </span>
                              </div>
                            )}

                            {/* Deductible Info */}
                            {result.deductible_info && (
                              <div className='text-sm space-y-0.5'>
                                {result.deductible_info
                                  .split(' | ')
                                  .map((part, idx) => (
                                    <div key={idx}>
                                      <span className='text-gray-700'>
                                        {part}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>

      {/* Eligibility Details Modal */}
      <EligibilityDetailsModal
        isOpen={isEligibilityModalOpen}
        onClose={() => setIsEligibilityModalOpen(false)}
        eligibilityResult={selectedEligibilityResult}
      />
    </Card>
  );
};

export default EligibilityCheckStep;
